import { useEffect, useMemo, useState } from "react";
import { baseProduct, canBook, containersText, defaultLocation, editsMasterData, fold, hasOwnPlace, lastPlace, quantityAt, inboundFromPurchase, inboundAt, inboundFromPdf, inboundTemplate, isSure, learnedArticles, lineQty, matchInboundLine, newArticleFields, parseInboundTable, PDF_CONVERTERS, uuidv7 } from "@lagerlog/core";
import type { Booking, Cell, Purchase, Inbound, InboundLine, InboundRecordLine, LineMatch } from "@lagerlog/core";
import { euro } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import { pdfItems } from "../pdf.ts";
import { IS_PANTRY, compareLagers } from "../brand.ts";
import { isTableFile, readTable, writePantryBook } from "../pantryBook.ts";
import { Emoji } from "./Emoji.tsx";

/** What happens with a line: an article, a new one, or nothing. */
type Choice = string | "new" | "skip";

interface Row {
  line: InboundLine;
  match: LineMatch;
  choice: Choice;
  lager: string;
  /** The storage is the article's own (where it lies, or how it is kept), not the one chosen for all (A5). */
  own: boolean;
  /** The quantity in the article's base unit, as typed; filled in where the document says it. */
  qty: string;
  bestBefore: string;
  open: boolean;
}

const LAGER_KEY = (format: string) => `lagerlog.inbound-lager.${format}`;
const day = (iso: string | undefined) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");
/** The quantity as on the document; a shopping trip's „2 Packung“ as the app writes it, „2 Pck.“ (B15). */
const amountText = (l: InboundLine, channel: Inbound["channel"]) => (channel === "shopping-list" ? containersText(l.qty, l.unit) : `${l.qty.toLocaleString("de-DE")} ${l.unit}`);

const isTable = isTableFile;

/** The rows of a table; the stock list of Vorräte is refused here, it would be stored again. */
async function tableRows(file: File): Promise<Cell[][]> {
  const { rows, stockList } = await readTable(file);
  if (stockList) throw new StockListError();
  return rows;
}

class StockListError extends Error {}

/** The template (E-3): the columns with three examples, and what each column means. */
const writeTemplate = () => writePantryBook(inboundTemplate(), "Vorrat", "VorratsBuch-Vorlage.xlsx");

function rememberedLager(format: string): string | null {
  try {
    return localStorage.getItem(LAGER_KEY(format));
  } catch {
    return null;
  }
}

/** The unit a line is counted in for the choice: the article's, or that of the new article. */
function unitOf(row: Row, catalog: Catalog): string {
  if (row.choice === "new") return row.line.total?.unit ?? "Stk.";
  return catalog.articles.get(row.choice)?.unit ?? "Stk.";
}

/** The quantity proposed for a choice, in its base unit; empty if only the person knows it (bananas by weight, counted in pieces). */
function proposedQty(line: InboundLine, choice: Choice, catalog: Catalog): string {
  if (choice === "skip") return "";
  if (choice === "new") return String(line.total?.qty ?? line.packs ?? "");
  const a = catalog.articles.get(choice);
  const q = a ? lineQty(line, a.fields) : null;
  return q === null ? "" : String(q);
}

const parsedQty = (row: Row) => {
  const n = Number(row.qty.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

/** ✓ sure, ? to check, neu, – not stored: as E3 shows them. */
function state(row: Row): { mark: string; cls: string; text: string } {
  if (row.choice === "skip") return { mark: "–", cls: "muted", text: "nicht einlagern" };
  if (parsedQty(row) === null) return { mark: "?", cls: "warn", text: "Menge fehlt" };
  if (row.choice === "new") return { mark: "neu", cls: "warn", text: "wird angelegt" };
  if (row.choice === row.match.article && isSure(row.match.state)) return { mark: "✓", cls: "ok", text: row.match.state === "learned" ? "wie beim letzten Mal" : "zugeordnet" };
  if (row.choice === row.match.article) return { mark: "?", cls: "warn", text: "bitte prüfen" };
  return { mark: "✓", cls: "ok", text: "gewählt" };
}

/**
 * Eingang (docs/18-vorratsbuch.md, Eingänge, E-2): a delivery note or invoice as PDF into the
 * pantry. The document is read on the device; each line goes to an article (learned per supplier,
 * same name, base product) or a new one, or is left out; storage and best-before per line; booked
 * as receipts with the price, and the Eingang is kept, so the same document never books twice.
 */
export function InboundView() {
  const { store } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const [doc, setDoc] = useState<{ inbound: Inbound; file: string } | null>(null);
  /** The storage for rows without a place of their own: new goods and those never stored. */
  const [above, setAbove] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ booked: number; left: number; created: number } | null>(null);
  const rights = store.rights();
  // Finished trips of the EinkaufsZettel (V1), after the last one taken in here.
  const [trips, setTrips] = useState<{ list: Purchase[]; error: string | null } | null>(null);
  const lastTrip = catalog ? Math.max(0, ...[...catalog.inbounds.values()].map((r) => Number(/^einkaufszettel:(\d+)$/.exec(r.key)?.[1] ?? 0))) : 0;
  useEffect(() => {
    if (!catalog || !editsMasterData(rights.role) || !IS_PANTRY) return;
    let gone = false;
    store
      .request<{ link: unknown }>("/api/workspace/einkauf")
      .then((s) => (s.link ? store.request<{ purchases: Purchase[] }>(`/api/workspace/einkauf/purchases?since=${lastTrip}`).then((r) => !gone && setTrips({ list: r.purchases, error: null })) : undefined))
      .catch((e: unknown) => !gone && setTrips({ list: [], error: e instanceof Error && !/fetch|abort/i.test(e.message) ? e.message : "Ohne Verbindung lassen sich die Einkäufe nicht abholen." }));
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog !== null, lastTrip]);

  const lagers = useMemo(() => {
    if (!catalog) return [];
    const held = new Set(store.config?.lagers ?? []);
    const byLager = compareLagers((id) => catalog.lagers.get(id)?.name ?? id);
    return [...catalog.lagers.values()].filter((l) => held.has(l.id) && canBook(rights, l.id)).sort((a, b) => byLager(a.id, b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, store.config]);

  /** The bookable locations of the inbound's storages, with an article's stock there. */
  const placesOf = (article: string | null, within?: string) =>
    [...(catalog?.locations.values() ?? [])]
      .filter((l) => (within ? l.lager === within : lagers.some((x) => x.id === l.lager)))
      .sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }))
      .map((l) => ({ id: l.id, lager: l.lager, stock: article && ledger ? quantityAt(ledger, article, l.id) : 0 }));

  /** The fields a row's goods have: the article's, or those it will get. */
  const fieldsOf = (choice: Choice, line: InboundLine, match: LineMatch) =>
    choice === "new" || choice === "skip" ? newArticleFields(line, baseProduct(match.base)) : (catalog?.articles.get(choice)?.fields ?? {});

  /** Where a row goes (A5): where the article lies or lay last, else where it is kept; else the storage above. */
  const placeFor = (choice: Choice, line: InboundLine, match: LineMatch, fallback: string): { lager: string; own: boolean } => {
    if (choice === "skip") return { lager: fallback, own: false };
    const article = choice === "new" ? null : choice;
    const places = placesOf(article);
    const fields = fieldsOf(choice, line, match);
    const last = article ? lastPlace(store.replica?.journal() ?? [], article) : undefined;
    if (!hasOwnPlace(fields, places, last)) return { lager: fallback, own: false };
    const id = defaultLocation({ fields, places, lagerProfile: (l) => catalog?.lagers.get(l)?.profile, lagerName: (l) => catalog?.lagers.get(l)?.name ?? l, last });
    return { lager: places.find((p) => p.id === id)?.lager ?? fallback, own: true };
  };

  if (!catalog) return null;
  if (!editsMasterData(rights.role)) {
    return (
      <section className="card">
        <h2>Beleg einlesen</h2>
        <p>Belege einlesen dürfen Inhaber und Verwaltung: Dabei entstehen neue Artikel, und die Preise stehen darin.</p>
      </section>
    );
  }
  if (lagers.length === 0) {
    return (
      <section className="card">
        <h2>Beleg einlesen</h2>
        <p>Auf diesem Gerät ist kein Lager, in das du einlagern darfst.</p>
      </section>
    );
  }

  const articles = [...catalog.articles.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
  const already = doc ? [...catalog.inbounds.values()].find((r) => r.key === doc.inbound.key) : undefined;

  /** An Eingangsbeleg into the review: lines matched, storage and best-before proposed. */
  function open(inbound: Inbound, label: string) {
      const learned = learnedArticles(catalog!.inbounds.values(), inbound.format);
      const remembered = rememberedLager(inbound.format);
      const lager = lagers.find((l) => l.id === remembered)?.id ?? lagers.find((l) => /kuehl/.test(l.id))?.id ?? lagers[0]!.id;
      setAbove(lager);
      const pantry = articles.map((a) => ({ id: a.id, fields: a.fields }));
      // A table may name the storage per row („Kühlschrank“); an unknown one takes the one above, with a note.
      const unknown = new Set<string>();
      const lagerOf = (place: string | undefined): string | null => {
        if (!place) return null;
        const found = lagers.find((l) => fold(l.name) === fold(place) || l.id === place);
        if (!found) unknown.add(place);
        return found?.id ?? lager;
      };
      setRows(
        inbound.lines.map((line) => {
          const match = matchInboundLine(line, pantry, learned);
          // Drinks are taken like any food (Martin, 05.11.2026: never refuse); „nicht einlagern“ stays a choice.
          const choice: Choice = match.state === "skip" ? "skip" : (match.article ?? "new");
          const named = lagerOf(line.place);
          const place = named ? { lager: named, own: true } : placeFor(choice, line, match, lager);
          return { line, match, choice, ...place, qty: proposedQty(line, choice, catalog!), bestBefore: line.bestBefore ?? "", open: false };
        }),
      );
      const notes = [...inbound.notes, ...[...unknown].map((p) => `Lager „${p}“ gibt es hier nicht; diese Zeilen gehen in das Lager oben.`)];
      setDoc({ inbound: { ...inbound, notes }, file: label });
  }

  async function read(file: File) {
    setError(null);
    setDone(null);
    setBusy(true);
    try {
      const inbound = isTable(file) ? parseInboundTable(await tableRows(file)) : inboundFromPdf(await pdfItems(await file.arrayBuffer()));
      if (!inbound) {
        setDoc(null);
        return setError(`Diesen Beleg kennt VorratsBuch noch nicht. Bisher: ${PDF_CONVERTERS.map((c) => c.label).join(", ")}.`);
      }
      open(inbound, file.name);
    } catch (e) {
      console.error(e);
      setDoc(null);
      if (e instanceof StockListError) return setError("Das ist die Bestandsliste aus „Vorräte“. Hier würde alles noch einmal eingelagert. Geänderte Angaben bitte unter Vorräte → „Tabelle hochladen“ übernehmen.");
      setError(isTable(file) ? "Die Tabelle ließ sich nicht lesen. Bitte als Excel-Datei (.xlsx) oder CSV speichern." : "Die Datei ließ sich nicht als PDF lesen.");
    } finally {
      setBusy(false);
    }
  }

  const update = (i: number, patch: Partial<Row>) => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const choose = (i: number, choice: Choice) => {
    const r = rows[i]!;
    const place = r.own && r.line.place ? { lager: r.lager, own: true } : placeFor(choice, r.line, r.match, above);
    update(i, { choice, ...place, qty: proposedQty(r.line, choice, catalog), open: r.open || (choice !== "skip" && proposedQty(r.line, choice, catalog) === "") });
  };
  const missing = rows.filter((r) => r.choice !== "skip" && parsedQty(r) === null).length;
  const toBook = rows.filter((r) => r.choice !== "skip").length;

  async function book() {
    if (!doc || already || missing > 0) return;
    setBusy(true);
    const { inbound } = doc;
    const recordId = uuidv7(Date.now());
    const at = inboundAt(inbound, Date.now());
    const lines: InboundRecordLine[] = [];
    let created = 0;
    for (const r of rows) {
      const base = { text: r.line.text, name: r.line.name };
      if (r.choice === "skip") {
        lines.push({ ...base, article: null });
        continue;
      }
      let article = r.choice;
      if (article === "new") {
        article = uuidv7(Date.now());
        await store.edit("article", article, newArticleFields(r.line, baseProduct(r.match.base)));
        created++;
      }
      // Within the storage, where the article lies or lay last (A5); else its first location.
      const inLager = placesOf(r.choice === "new" ? null : article, r.lager);
      const where = inLager.length ? defaultLocation({ fields: {}, places: inLager, lagerProfile: () => undefined, lagerName: () => "", last: lastPlace(store.replica?.journal() ?? [], article) }) : "";
      const location = catalog!.locations.get(where);
      const qty = parsedQty(r)!;
      if (!location) {
        lines.push({ ...base, article });
        continue;
      }
      const booking = await store.book({
        kind: "in",
        article,
        lager: r.lager,
        location: location.id,
        qty,
        reason: "purchase",
        at,
        ...(r.line.amount !== undefined ? { amount: r.line.amount } : {}),
        note: `inbound:${recordId}`,
        lotInfo: { ...(inbound.supplier ? { supplier: inbound.supplier } : {}), ...(r.bestBefore ? { bestBefore: r.bestBefore } : {}) },
      } as Booking);
      lines.push({ ...base, article, qty, booking: booking.id });
    }
    const person = store.config?.userId;
    await store.edit("inbound", recordId, {
      key: inbound.key,
      format: inbound.format,
      kind: inbound.kind,
      ...(inbound.supplier ? { supplier: inbound.supplier } : {}),
      ...(inbound.ref ? { ref: inbound.ref } : {}),
      ...(inbound.date ? { date: inbound.date } : {}),
      file: doc.file,
      at: Date.now(),
      ...(person ? { person } : {}),
      lines,
      ...(inbound.goods !== undefined ? { goods: inbound.goods } : {}),
      ...(inbound.total !== undefined ? { total: inbound.total } : {}),
    });
    try {
      localStorage.setItem(LAGER_KEY(inbound.format), above);
    } catch {
      // Only a convenience.
    }
    setDone({ booked: lines.filter((l) => l.booking).length, left: lines.filter((l) => l.article === null).length, created });
    setDoc(null);
    setRows([]);
    setBusy(false);
  }

  return (
    <div className="inbound">
      <a className="link" href="#/start">
        ← Start
      </a>
      <h2>Beleg einlesen</h2>
      {done && (
        <section className="card" data-testid="inbound-done">
          <strong>Eingelagert</strong>
          <p>
            {done.booked} {done.booked === 1 ? "Position" : "Positionen"} eingelagert{done.created ? `, davon ${done.created} neu angelegt` : ""}
            {done.left ? `; ${done.left} nicht eingelagert` : ""}.
          </p>
          <div className="buttons">
            <a className="button primary" href="#/vorraete">
              Zu den Vorräten
            </a>
          </div>
        </section>
      )}
      {trips && (trips.error || trips.list.length > 0) && (
        <section className="card" data-testid="inbound-trips">
          <strong>Einkäufe vom EinkaufsZettel</strong>
          {trips.error && <p className="error-box">{trips.error}</p>}
          <ul className="list">
            {trips.list
              .filter((p) => ![...catalog.inbounds.values()].some((r) => r.key === `einkaufszettel:${p.id}`))
              .map((p) => (
                <li key={p.id}>
                  <button className="row between" onClick={() => [setDone(null), open(inboundFromPurchase(p), `Einkauf ${p.list.name}`)]} data-testid={`inbound-trip-${p.id}`}>
                    <span>
                      Einkauf vom {day(p.at.slice(0, 10))}
                      <br />
                      <span className="muted">
                        {p.list.name}
                        {p.by ? ` · ${p.by}` : ""}
                      </span>
                    </span>
                    <span className="side">{p.items.length === 1 ? "1 Posten" : `${p.items.length} Posten`}</span>
                  </button>
                </li>
              ))}
          </ul>
          <p className="hint">Was im EinkaufsZettel als gekauft abgeschlossen ist. Ein Tipp öffnet den Einkauf zum Einlagern, Getränke wie alles andere; Wein, Bier und Spirituosen führst du besser in der App KellerLog.</p>
        </section>
      )}
      <section className="card">
        <p>Rechnung oder Lieferschein als PDF, oder eine Tabelle</p>
        {/* Our own button instead of the browser's „Datei auswählen – Keine ausgewählt“ (C6). */}
        <label className={`button primary wide file-pick ${busy ? "disabled" : ""}`} data-testid="inbound-pick">
          {busy ? "Wird gelesen …" : "PDF oder Tabelle wählen …"}
          <input
            type="file"
            accept="application/pdf,.pdf,.xlsx,.csv"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void read(file);
            }}
            data-testid="inbound-file"
          />
        </label>
        <p className="hint">Gelesen wird auf diesem Gerät, die Datei bleibt hier. Am iPhone: in Mail den Anhang „In Dateien sichern“, dann hier wählen. PDF bisher von: {PDF_CONVERTERS.map((c) => c.label).join(", ")}.</p>
        <p className="hint">
          Tabelle (Excel oder CSV): eine Zeile je Ware mit Bezeichnung und Menge, mehr nach Belieben.{" "}
          <button type="button" className="link" onClick={() => void writeTemplate()} data-testid="inbound-template">
            Vorlage herunterladen
          </button>
        </p>
        {busy && !doc && <p className="muted">Lese den Beleg …</p>}
        {error && <p className="error-box">{error}</p>}
      </section>
      {doc && (
        <>
          <section className="card" data-testid="inbound-head">
            <strong>
              {doc.inbound.channel === "table" ? `Tabelle · ${doc.file}` : doc.inbound.channel === "shopping-list" ? `${doc.file} · ${day(doc.inbound.date)}` : `${doc.inbound.supplier} · ${doc.inbound.kind === "order" ? "Bestellbestätigung" : "Lieferung"} ${doc.inbound.ref ?? ""}`}
            </strong>
            <p className="muted">
              {doc.inbound.date ? `geliefert ${day(doc.inbound.date)} · ` : ""}
              {doc.inbound.lines.length} Positionen
              {doc.inbound.goods !== undefined ? ` · Waren ${euro(doc.inbound.goods)}` : ""}
              {doc.inbound.fee !== undefined ? ` · Liefergebühr ${euro(doc.inbound.fee)}` : ""}
            </p>
            {doc.inbound.notes.map((n) => (
              <p key={n} className="notice">
                {n}
              </p>
            ))}
            {already && (
              <p className="notice" data-testid="inbound-already">
                {doc.inbound.channel === "table" ? "Diese Tabelle" : "Diesen Beleg"} hast du am {new Date(already.at).toLocaleDateString("de-DE")} schon eingelesen. Nichts davon wird noch einmal gebucht.
              </p>
            )}
            {doc.inbound.kind === "order" && (
              <p className="notice" data-testid="inbound-order">
                Das ist eine Bestellbestätigung: Die Gewichte sind geschätzt, getauscht wird erst beim Packen. Eingelagert wird nach der Rechnung oder dem Lieferschein.
              </p>
            )}
            {doc.inbound.kind === "delivery" && !already && (
              <label>
                Einlagern in
                <select
                  value={above}
                  onChange={(e) => {
                    setAbove(e.target.value);
                    setRows((all) => all.map((r) => (r.own ? r : { ...r, lager: e.target.value })));
                  }}
                  data-testid="inbound-lager"
                >
                  {lagers.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <span className="hint">Für neue Vorräte und solche ohne festen Platz. Was schon einen Platz hat, geht dorthin; einzelne lassen sich unten ändern.</span>
              </label>
            )}
          </section>
          <ul className="list card inbound-lines" data-testid="inbound-lines">
            {rows.map((r, i) => (
              <InboundRow key={i} i={i} row={r} catalog={catalog} articles={articles} lagers={lagers} readOnly={doc.inbound.kind === "order" || !!already} channel={doc.inbound.channel} choose={choose} update={update} />
            ))}
          </ul>
          {doc.inbound.kind === "delivery" && !already && (
            <>
              {missing > 0 && <p className="warn">Bei {missing === 1 ? "einer Position fehlt" : `${missing} Positionen fehlt`} die Menge.</p>}
              <button className="primary inbound-book" disabled={busy || missing > 0 || toBook === 0} onClick={() => void book()} data-testid="inbound-book">
                {toBook === 1 ? "1 Position einlagern" : `${toBook} Positionen einlagern`}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function InboundRow({
  i,
  row,
  catalog,
  articles,
  lagers,
  readOnly,
  channel,
  choose,
  update,
}: {
  i: number;
  row: Row;
  catalog: Catalog;
  articles: Article[];
  lagers: { id: string; name: string }[];
  readOnly: boolean;
  channel: Inbound["channel"];
  choose: (i: number, c: Choice) => void;
  update: (i: number, patch: Partial<Row>) => void;
}) {
  const s = state(row);
  const l = row.line;
  const near = row.match.candidates.map((id) => catalog.articles.get(id)).filter((a): a is Article => !!a);
  // The base product in brackets only where it says more than the name: „Neu: Kiwi“, „Neu: Birnen Conference (Birne)“.
  const baseName = row.match.base ? baseProduct(row.match.base)?.names[0] : undefined;
  const newName = `Neu: ${l.name}${baseName && fold(baseName) !== fold(l.name) ? ` (${baseName})` : ""}`;
  // The picture of what the line becomes: the chosen article, or the base product guessed for a new one.
  const chosenFields = row.choice !== "new" && row.choice !== "skip" ? (catalog.articles.get(row.choice)?.fields ?? {}) : { base: row.match.base, ...(l.organic ? { organic: true } : {}) };
  // In the base unit, not in packs: 6 eggs of a box of 10 are no „opened box“ here.
  const qty = Number(row.qty);
  return (
    <li className="entry-block" data-testid={`inbound-line-${i}`}>
      <div className="row between">
        <span className="entry-text inbound-name">
          <span className={`mark ${s.cls}`}>{s.mark}</span>
          <Emoji fields={chosenFields} />
          <span>
            {l.text}
            {l.replaces && <span className="muted"> · statt {l.replaces}</span>}
          </span>
        </span>
        <span className="side">
          <span>{amountText(l, channel)}</span>
          {l.amount !== undefined && <span className="muted">{euro(l.amount)}</span>}
        </span>
      </div>
      {!readOnly && (
        <div className="inbound-choice">
          <select value={row.choice} onChange={(e) => choose(i, e.target.value)} aria-label={`Artikel für ${l.text}`} data-testid={`inbound-choice-${i}`}>
            {near.length > 0 && (
              <optgroup label="Passend">
                {near.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </optgroup>
            )}
            <option value="new">{newName}</option>
            <option value="skip">Nicht einlagern</option>
            <optgroup label="Alle Vorräte">
              {articles
                .filter((a) => !row.match.candidates.includes(a.id))
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </optgroup>
          </select>
          <span className={`hint ${s.cls === "warn" ? "warn" : ""}`}>
            {s.text}
            {row.choice !== "skip" && parsedQty(row) !== null && ` · ${qty.toLocaleString("de-DE")} ${unitOf(row, catalog)}`}
            {row.choice !== "skip" && (
              <>
                {" · "}
                <button type="button" className="link" onClick={() => update(i, { open: !row.open })} data-testid={`inbound-more-${i}`}>
                  {row.open ? "weniger" : "Menge, Lager, MHD"}
                </button>
              </>
            )}
          </span>
          {row.open && row.choice !== "skip" && (
            <div className="inbound-details">
              <label>
                Menge in {unitOf(row, catalog)}
                <input inputMode="numeric" value={row.qty} onChange={(e) => update(i, { qty: e.target.value })} placeholder={unitOf(row, catalog) === "Stk." ? "z. B. 4" : ""} data-testid={`inbound-qty-${i}`} />
              </label>
              <label>
                Lager
                <select value={row.lager} onChange={(e) => update(i, { lager: e.target.value, own: true })} data-testid={`inbound-row-lager-${i}`}>
                  {lagers.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                MHD
                <input type="date" value={row.bestBefore} onChange={(e) => update(i, { bestBefore: e.target.value })} />
              </label>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
